// JAPAN WEB CLINIC (Dawn) — rules shared by the product page, the receiving step, the cart and the top page:
// money, dates in Japan time, the shipping schedule and countdown, the receiving step's checks, cart attributes, and
// reading / saving the server cart. Settings come from snippets/jwc-settings.liquid (Theme settings → JWC).
// Moved from the previous theme (theme/assets/jwc-core.js); the receiving parts are rebuilt in step ④ of the plan.
window.JWC = (() => {
  'use strict';

  // Staff read the Japanese keys on the order; okurun reads the English keys (okurun handoff §4, revision 12). Both are
  // written from the same receiving state, so they never disagree.
  const ATTR = { visitor: '旅行中', resident: '日本在住', delivery: '配送（佐川クール便）', pickup: '大阪院で受け取り' };
  const ENGLISH_KEYS = ['passport_name', 'receive_method', 'stay_type', 'hotel_name', 'reservation_name', 'hotel_phone', 'check_in_date', 'check_out_date', 'preferred_delivery_date', 'preferred_delivery_time', 'departure_date'];
  const RECEIPT_KEYS = ['在住/旅行', '受け取り方法', '滞在先', '宿泊先', '到着日', '出発日', '帰国日', '希望日', '希望時間帯', ...ENGLISH_KEYS];

  // ---------- text ----------
  // `| t` output is HTML-escaped (' → &#39;); undo it before using the text in the DOM.
  const decoder = document.createElement('textarea');
  const decode = (s) => {
    decoder.innerHTML = s == null ? '' : String(s);
    return decoder.value;
  };
  const texts = (raw) => Object.fromEntries(Object.entries(raw || {}).map(([k, v]) => [k, decode(v)]));
  // {name} (locale strings; `| t` would fill {{ name }} itself) or {{ name }}
  const fill = (text, vars) => String(text).replace(/\{\{\s*(\w+)\s*\}\}|\{(\w+)\}/g, (m, a, b) => ((a || b) in vars ? vars[a || b] : m));
  const intlLocale = (locale) => ({ ja: 'ja-JP', en: 'en-US', 'zh-TW': 'zh-TW', 'zh-CN': 'zh-CN' })[locale] || locale;
  function decodeRules(rules) {
    for (const rule of Object.values(rules || {})) for (const k of ['name', 'note', 'period']) if (rule[k]) rule[k] = decode(rule[k]);
    return rules;
  }

  function money(cents, format) {
    const n = Number(cents) / 100;
    const group = (v, d) => v.toFixed(d).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decode(format).replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) => {
      if (k === 'amount') return group(n, 2);
      if (k === 'amount_with_comma_separator') return group(n, 2).replace(/,/g, '.').replace(/\.(\d{2})$/, ',$1');
      return group(n, 0);
    });
  }

  // F-79: a price in the visitor's money as a rough guide, like snippets/jwc-fx.liquid (fx = JwcSettings.fx: the rate,
  // the rounding unit and the words; null on the Japanese pages or without a recent rate → '')
  function fxText(cents, fx) {
    if (!fx || !fx.rate) return '';
    const unit = Number(fx.unit) || 1;
    const v = Math.round(((Number(cents) || 0) / 100) * fx.rate / unit) * unit;
    return fill(decode(fx.approx), { amount: String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ',') });
  }

  // ---------- dates: 'YYYY-MM-DD' strings for days in Japan, arithmetic in UTC ----------
  const DAY = 86400000;
  const toMs = (d) => {
    const [y, m, dd] = d.split('-').map(Number);
    return Date.UTC(y, m - 1, dd);
  };
  const addDays = (d, n) => new Date(toMs(d) + n * DAY).toISOString().slice(0, 10);
  const validDate = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) && new Date(toMs(v)).toISOString().slice(0, 10) === v;
  // A fixed clock for checking: Theme setting "テスト用の「今」" ("2026-10-15" or "2026-10-15 14:52"), or on an
  // unpublished theme (preview) ?jst=2026-10-15T14:52. The fixed clock runs on from page load.
  let fixedClock = null;
  function setTestClock(text) {
    const m = String(text || '').trim().match(/^(\d{4}-\d{2}-\d{2})(?:[ T](\d{1,2}):(\d{2}))?$/);
    fixedClock = m && validDate(m[1]) ? { date: m[1], sec: (Number(m[2] || 0) * 60 + Number(m[3] || 0)) * 60, at: Date.now() } : null;
    return Boolean(fixedClock);
  }
  // { date: 'YYYY-MM-DD', minutes, seconds } in Japan, whatever the device's time zone (acceptance S02)
  function tokyoNow() {
    if (fixedClock) {
      const sec = fixedClock.sec + Math.floor((Date.now() - fixedClock.at) / 1000);
      const s = sec % 86400;
      return { date: addDays(fixedClock.date, Math.floor(sec / 86400)), minutes: Math.floor(s / 60), seconds: s };
    }
    const parts = {};
    new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' })
      .formatToParts(new Date())
      .forEach((p) => (parts[p.type] = p.value));
    const seconds = (Number(parts.hour) % 24) * 3600 + Number(parts.minute) * 60 + Number(parts.second);
    return { date: `${parts.year}-${parts.month}-${parts.day}`, minutes: Math.floor(seconds / 60), seconds };
  }
  // "2026-12-31" or "2026-12-30~2027-01-03", one per line
  function dateSet(text) {
    const set = {};
    String(text || '').split(/\r?\n|,/).forEach((line) => {
      const m = line.trim().match(/^(\d{4}-\d{2}-\d{2})\s*(?:[~〜～]\s*(\d{4}-\d{2}-\d{2}))?$/);
      if (!m) return;
      for (let d = m[1], i = 0; d <= (m[2] || m[1]) && i < 400; d = addDays(d, 1), i++) set[d] = true;
    });
    return set;
  }
  // "北海道=1", one per line
  function extraDays(text) {
    const map = {};
    String(text || '').split(/\r?\n|,/).forEach((line) => {
      const [k, v] = line.split('=');
      if (k && v !== undefined) map[k.trim()] = parseInt(v, 10) || 0;
    });
    return map;
  }
  const formatDate = (d, locale) => (validDate(d) ? new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', weekday: 'short', timeZone: 'UTC' }).format(new Date(toMs(d))) : '—');
  const lines = (text) => String(text || '').split(/\r?\n/).map((s) => s.trim()).filter(Boolean);

  // "17:30" → minutes after midnight (null when not a time)
  const hm = (v) => {
    const m = String(v || '').trim().match(/^(\d{1,2}):(\d{2})$/);
    return m && Number(m[1]) < 24 && Number(m[2]) < 60 ? Number(m[1]) * 60 + Number(m[2]) : null;
  };

  // Delivery / pickup dates from Theme settings (cutoff 15:00, no closed days: owner 2026-10-14; okurun keeps the same
  // values). The dates are the customer's preference; the date is confirmed after the doctor approves.
  // Osaka pick-up has its own same-day cutoff (plan v3 §3E, F-56): slots 10:00〜18:30 every 30 minutes, today's guidance
  // and confirmation until 17:30 (Japan time), and today only the slots later than now + 60 minutes (theme settings).
  // returnMorning (theme setting, off until okurun and Sagawa agree, §7-8): a delivery on the return day or the
  // check-out day is accepted only with the time code 'morning'; while off, such a delivery does not make it.
  function makeSchedule(s) {
    const now = tokyoNow();
    const today = now.date;
    const cutoff = Number(s.cutoffHour) * 60;
    const before = now.minutes < cutoff;
    const noShip = dateSet(s.noShipDates);
    const closed = dateSet(s.pickupClosed);
    const extra = extraDays(s.extraDays);
    const shipDay = (d) => {
      const w = new Date(toMs(d)).getUTCDay();
      return !noShip[d] && !(s.noShipSat && w === 6) && !(s.noShipSun && w === 0);
    };
    const open = (d) => !closed[d];
    const firstFrom = (d, ok) => {
      for (let i = 0; i < 60 && !ok(d); i++) d = addDays(d, 1);
      return d;
    };
    const firstShip = before && shipDay(today) ? today : firstFrom(addDays(today, 1), shipDay);
    const pickupSlots = lines(s.pickupSlots).filter((v) => hm(v) !== null);
    const pickupCutoff = hm(s.pickupCutoff) ?? 17 * 60 + 30;
    const lead = Number.isFinite(Number(s.pickupLead)) && String(s.pickupLead ?? '') !== '' ? Number(s.pickupLead) : 60;
    // a slot can be chosen on that day: any slot on a later day; today only before the cutoff and after now + lead
    const pickupSlotOk = (d, v) => {
      if (!pickupSlots.includes(v) || !open(d)) return false;
      if (d !== today) return d > today;
      return now.minutes < pickupCutoff && hm(v) > now.minutes + lead;
    };
    const firstPickup = pickupSlots.some((v) => pickupSlotOk(today, v)) ? today : firstFrom(addDays(today, 1), open);
    return {
      today, now, cutoffHour: Number(s.cutoffHour), shipDay, firstShip, open, firstPickup,
      pickupEnabled: s.pickupEnabled !== false, pickupSlots, pickupCutoff, pickupLead: lead, pickupSlotOk,
      returnMorning: Boolean(s.returnMorning),
      noShip: (d) => Boolean(noShip[d]),
      extraDays: (pref) => extra[pref] || 0,
      earliestDelivery: (pref) => addDays(firstShip, 1 + (extra[pref] || 0)),
    };
  }

  // Countdown to the next shipping cutoff (owner F-14): before the cutoff on a shipping day → 'today'; after it, when
  // today and tomorrow both ship → 'tomorrow'; otherwise (a no-ship day) nothing. warn = under 10 minutes to today's.
  function countdown(s) {
    const now = tokyoNow();
    const sch = makeSchedule(s);
    const cut = sch.cutoffHour * 3600;
    let key = null;
    let left = 0;
    if (sch.shipDay(now.date) && now.seconds < cut) {
      key = 'today';
      left = cut - now.seconds;
    } else if (sch.shipDay(now.date) && sch.shipDay(addDays(now.date, 1))) {
      key = 'tomorrow';
      left = 86400 - now.seconds + cut;
    }
    const minutes = Math.ceil(left / 60);
    return { key, minutes, hours: Math.floor(minutes / 60), rest: minutes % 60, warn: key === 'today' && left < 600, cutoffHour: sch.cutoffHour };
  }

  // ---------- dates for people ----------
  // T.date_short "{{ month }}月{{ day }}日（{{ weekday }}）", T.date_md "{{ month }}/{{ day }}", T.weekdays "日,月,…"
  function dateText(d, T, key = 'date_short') {
    if (!validDate(d)) return '—';
    const w = new Date(toMs(d)).getUTCDay();
    return fill(T[key] || '{{ month }}/{{ day }}', { month: Number(d.slice(5, 7)), day: Number(d.slice(8)), weekday: String(T.weekdays || '').split(',')[w] || '' });
  }
  const nights = (from, to) => (validDate(from) && validDate(to) ? Math.round((toMs(to) - toMs(from)) / DAY) : 0);

  // ---------- receiving (okurun handoff §4・§4-1) ----------
  // State: { stay: traveling|resident, method: delivery|pickup, pref, hotel, reservation, phone, checkin, checkout,
  //          returnDate, date, time, pickupDate, pickupTime, passport, destination }
  // Delivery time codes are okurun's: '' (no preference), morning, 12-14, 14-16, 16-18, 18-20, 19-21.
  const slotJa = (code) => (!code ? '指定なし' : code === 'morning' ? '午前中（8:00–12:00）' : /^\d{1,2}-\d{1,2}$/.test(code) ? code.split('-').map((h) => `${h}:00`).join('–') : code);
  // passport_name: English letters, spaces, hyphens and apostrophes only; saved trimmed and in capitals
  const passportName = (v) => String(v || '').trim().replace(/\s+/g, ' ').toUpperCase();
  const validPassportName = (v) => /^[A-Z][A-Z' -]*$/.test(passportName(v));
  // hotel_phone: digits, hyphens, spaces and a leading +81 only; saved as digits with +81 → 0 (okurun 2026-10-15),
  // which must be 10–11 digits starting with 0
  function japanPhone(v) {
    const raw = String(v || '').trim();
    if (!raw) return { value: '', ok: false, empty: true };
    if (!/^(\+81)?[\d\s-]+$/.test(raw)) return { value: '', ok: false };
    let digits = raw.replace(/[\s-]/g, '');
    if (digits.startsWith('+81')) digits = `0${digits.slice(3).replace(/^0/, '')}`;
    return { value: digits, ok: /^0\d{9,10}$/.test(digits) };
  }

  function receiptFromAttributes(a) {
    if (!a || !(a.receive_method || a['受け取り方法'])) return null;
    const pickup = a.receive_method ? a.receive_method === 'pickup' : a['受け取り方法'] === ATTR.pickup;
    const resident = a.stay_type ? a.stay_type === 'resident' : a['在住/旅行'] === ATTR.resident;
    // the surname and given names as typed (JWC-08), while they still make the saved passport_name; carts saved before
    // they were kept have passport_name only, which is never split at a space (「VAN DER BERG」「JAN」)
    const surname = passportName(a.jwc_passport_surname);
    const given = passportName(a.jwc_passport_given);
    const names = surname && [surname, given].filter(Boolean).join(' ') === passportName(a.passport_name) ? { passportSurname: surname, passportGiven: given } : {};
    return {
      stay: resident ? 'resident' : 'traveling',
      method: pickup ? 'pickup' : 'delivery',
      pref: pickup ? '' : a['滞在先'] || '',
      hotel: a.hotel_name || '',
      reservation: a.reservation_name || '',
      phone: a.hotel_phone || '',
      checkin: a.check_in_date || '',
      checkout: a.check_out_date || '',
      returnDate: a['帰国日'] || '',
      date: pickup ? '' : a.preferred_delivery_date || '',
      time: pickup ? '' : a.preferred_delivery_time || '',
      pickupDate: pickup ? a['希望日'] || '' : '',
      pickupTime: pickup ? a['希望時間帯'] || '' : '',
      passport: a.passport_name || '',
      ...names,
      destination: a.jwc_carry_destination || '',
    };
  }

  // ctx: { schedule, T, pickupEnabled, deliverySlots, pickupSlots }  → { field, message } | null (the first issue, in
  // the order of the form). T holds the jwc.receive strings (err_*), dates are written with dateText.
  function receiptIssue(s, ctx) {
    const { schedule, T } = ctx;
    const day = (d) => dateText(d, T);
    if (s.stay !== 'traveling' && s.stay !== 'resident') return { field: 'stay', message: T.err_stay };
    if (s.method === 'pickup') {
      if (!ctx.pickupEnabled) return { field: 'method', message: T.err_pickup_date };
      if (!validDate(s.pickupDate)) return { field: 'pickupDate', message: T.err_pickup_date };
      if (s.pickupDate < schedule.firstPickup) return { field: 'pickupDate', message: fill(T.err_date_early, { date: day(schedule.firstPickup) }) };
      if (!schedule.open(s.pickupDate)) return { field: 'pickupDate', message: T.err_pickup_closed };
      if (!ctx.pickupSlots.includes(s.pickupTime)) return { field: 'pickupTime', message: T.err_pickup_time };
      // today: before the same-day cutoff and later than now + the lead time only (F-56)
      if (!schedule.pickupSlotOk(s.pickupDate, s.pickupTime)) return { field: 'pickupTime', message: T.err_pickup_time_soon };
    } else {
      if (!s.pref) return { field: 'pref', message: T.err_pref };
      const earliest = schedule.earliestDelivery(s.pref);
      const traveling = s.stay === 'traveling';
      if (traveling) {
        if (!String(s.hotel || '').trim()) return { field: 'hotel', message: T.err_hotel_name };
        const phone = japanPhone(s.phone);
        if (phone.empty) return { field: 'phone', message: T.err_hotel_phone };
        if (!phone.ok) return { field: 'phone', message: T.err_hotel_phone_format };
        if (s.reservation && !String(s.reservation).trim()) return { field: 'reservation', message: T.err_reservation_name };
        if (!validDate(s.checkin)) return { field: 'checkin', message: T.err_checkin };
        if (s.checkin < schedule.today) return { field: 'checkin', message: T.err_checkin_past };
        if (!validDate(s.checkout) || s.checkout <= s.checkin) return { field: 'checkout', message: T.err_checkout };
        // the check-out day itself counts only while morning delivery is accepted (F-46, switch §7-8)
        const tooShort = schedule.returnMorning ? earliest > s.checkout : earliest >= s.checkout;
        if (tooShort) return { field: 'checkout', message: fill(T.err_short_stay, { date: day(earliest) }) };
      }
      if (!validDate(s.date)) return { field: 'date', message: T.err_date };
      if (s.date < earliest) return { field: 'date', message: fill(T.err_date_early, { date: day(earliest) }) };
      if (traveling) {
        if (s.date < s.checkin || s.date > s.checkout || (s.date === s.checkout && !schedule.returnMorning)) {
          return { field: 'date', message: schedule.returnMorning ? T.err_date_stay_checkout : T.err_date_stay };
        }
      }
      // the time code okurun reads: '' (no preference) or one of the settings' codes
      if (s.time && !ctx.deliverySlots.includes(s.time)) return { field: 'time', message: T.err_time };
      // on the check-out day, morning only (judged by the saved code, never by the label or the option's place)
      if (traveling && s.date === s.checkout && s.time !== 'morning') return { field: 'time', message: fill(T.err_checkout_morning, { morning: T.slot_morning || '' }) };
    }
    // the form's two fields (F-66), or the one saved passport_name (the cart)
    if (s.passportSurname !== undefined) {
      if (!String(s.passportSurname || '').trim()) return { field: 'passportSurname', message: T.err_passport_surname || T.err_passport_name };
      if (!validPassportName(s.passportSurname)) return { field: 'passportSurname', message: T.err_passport_name_format };
      if (String(s.passportGiven || '').trim() && !validPassportName(s.passportGiven)) return { field: 'passportGiven', message: T.err_passport_name_format };
      return null;
    }
    if (!String(s.passport || '').trim()) return { field: 'passport', message: T.err_passport_name };
    if (!validPassportName(s.passport)) return { field: 'passport', message: T.err_passport_name_format };
    return null;
  }

  // The certificate's line item properties (okurun handoff §5). "Name on passport" is the order's passport_name.
  // c: { native, dob, nationality, number, address, sex }. Sex (revision 13) is optional and saved only as Female / Male.
  function certificateIssue(c, today, T) {
    if (!validDate(c.dob)) return { field: 'certDob', message: T.err_cert_dob };
    if (c.dob > today) return { field: 'certDob', message: T.err_cert_dob_future };
    if (!String(c.nationality || '').trim()) return { field: 'certNationality', message: T.err_cert_nationality };
    if (!String(c.number || '').trim()) return { field: 'certNumber', message: T.err_cert_passport_number };
    if (!/^[A-Za-z0-9]+$/.test(String(c.number).trim())) return { field: 'certNumber', message: T.err_cert_passport_number_format };
    if (!String(c.address || '').trim()) return { field: 'certAddress', message: T.err_cert_address };
    return null;
  }
  function certificateProperties(c, passport) {
    return {
      'Name on passport': passportName(passport),
      'Native name': String(c.native || '').trim(),
      'Date of birth': c.dob,
      Nationality: String(c.nationality || '').trim(),
      'Passport number': String(c.number || '').trim().toUpperCase(),
      'Home address': String(c.address || '').trim(),
      ...(CERT_SEX.includes(c.sex) ? { Sex: c.sex } : {}),
    };
  }
  const CERT_SEX = ['Female', 'Male'];

  // The order's attributes. Fields that do not apply are '' (an empty value removes the attribute), so changing the
  // way of receiving clears what the old way had (acceptance R06).
  function receiptAttributes(s, carryTotal, locale) {
    const pickup = s.method === 'pickup';
    const traveling = s.stay === 'traveling';
    const hotel = !pickup && traveling;
    return {
      '在住/旅行': traveling ? ATTR.visitor : ATTR.resident,
      受け取り方法: pickup ? ATTR.pickup : ATTR.delivery,
      滞在先: pickup ? '' : s.pref,
      宿泊先: hotel ? String(s.hotel).trim() : '',
      到着日: hotel ? s.checkin : '',
      出発日: hotel ? s.checkout : '',
      帰国日: traveling ? s.returnDate || '' : '',
      希望日: pickup ? s.pickupDate : s.date,
      希望時間帯: pickup ? s.pickupTime : slotJa(s.time),
      表示言語: locale,
      passport_name: passportName(s.passport),
      // the form's two fields as typed, for editing again and checkout's names (JWC-08); passport_name stays the order's
      jwc_passport_surname: passportName(s.passportSurname),
      jwc_passport_given: passportName(s.passportGiven),
      receive_method: pickup ? 'pickup' : 'delivery',
      stay_type: traveling ? 'traveling' : 'resident',
      hotel_name: hotel ? String(s.hotel).trim() : '',
      reservation_name: hotel ? String(s.reservation || '').trim() : '',
      hotel_phone: hotel ? japanPhone(s.phone).value : '',
      check_in_date: hotel ? s.checkin : '',
      check_out_date: hotel ? s.checkout : '',
      preferred_delivery_date: pickup ? '' : s.date,
      preferred_delivery_time: pickup ? '' : s.time || '',
      // the day of leaving Japan = the return date's field (okurun prints it on the certificate only, revision 13);
      // travellers who pick up at the clinic are asked it too (owner 2026-10-22)
      departure_date: traveling ? s.returnDate || '' : '',
      jwc_carry_schema_version: '1',
      jwc_carry_destination: s.destination,
      jwc_carry_count_mode: 'order',
      jwc_carry_total_pens: String(carryTotal),
    };
  }

  // ctx: { T (receive + place strings), slotLabel(code), prefLabel(pref) } → summary values for people
  function receiptValues(s, ctx) {
    const { T } = ctx;
    const pickup = s.method === 'pickup';
    const date = pickup ? s.pickupDate : s.date;
    const place = pickup ? T.place_pickup : fill(s.stay === 'resident' ? T.place_home : T.place_hotel, { pref: ctx.prefLabel ? ctx.prefLabel(s.pref) : s.pref });
    const time = pickup ? s.pickupTime || '—' : ctx.slotLabel(s.time);
    const values = { place, date: dateText(date, T), time };
    values.summary = [place, values.date, time].join(' · ');
    return values;
  }

  // ---------- will it arrive (WF top ② and the receiving step; F-07・F-22・F-36・F-44・F-46) ----------
  // One place for the top's arrival check, the receiving step and the cart (plan v3 §3E). A delivery the day before
  // the return (or earlier) is in time. On the return day / check-out day itself it is "tight": in time only with the
  // consultation done by the cutoff and a morning delivery — and only while schedule.returnMorning is on; while off it
  // does not make it. When the delivery does not make it, Osaka pick-up is offered if it can be done by that day.
  // Answers that stop the guest ('ng') are worded as the next step, not as a refusal (owner 2026-10-22: 「間に合わないかも
  // しれません」 makes people leave): the earliest delivery day, and Osaka for sooner; soft: true = shown without the ✕.
  const shipDayFor = (schedule, pref, date) => {
    let last = addDays(date, -1 - schedule.extraDays(pref));
    for (let i = 0; i < 30 && schedule.noShip(last); i++) last = addDays(last, -1);
    return last;
  };
  // the word for the day of the consultation's cutoff: 今日 / 明日 / M/D
  const dayWord = (schedule, d, T) => (d === schedule.today ? T.arr_today : d === addDays(schedule.today, 1) ? T.arr_tomorrow : dateText(d, T, 'date_md'));
  // Osaka pick-up by `by` (the return day, else the check-out day): the same rules as the pick-up form (cutoff, lead,
  // closed days, slots); the text names the first and last slot
  function osakaLine(schedule, by, T) {
    if (!schedule.pickupEnabled || !validDate(by) || by < schedule.today || schedule.firstPickup > by || !schedule.pickupSlots.length) return '';
    return fill(T.arr_osaka, { first: schedule.pickupSlots[0], last: schedule.pickupSlots[schedule.pickupSlots.length - 1] });
  }
  function arrival(schedule, pref, ret, T) {
    if (!pref || !ret) return { state: 'empty' };
    if (!validDate(ret) || ret < schedule.today) return { state: 'ng', invalid: true, headline: T.arr_invalid };
    const extra = schedule.extraDays(pref);
    const earliest = schedule.earliestDelivery(pref);
    const md = (d) => dateText(d, T, 'date_md');
    const morning = T.slot_morning || '';
    if (earliest > ret || (earliest === ret && !schedule.returnMorning)) {
      const osaka = osakaLine(schedule, ret, T);
      return { state: 'ng', soft: true, headline: fill(T.arr_ng_head, { date: dateText(earliest, T) }), when: osaka ? '' : T.arr_ng_body, osaka };
    }
    if (earliest === ret) {
      const day = dayWord(schedule, shipDayFor(schedule, pref, ret), T);
      return {
        state: 'ok', tight: true, guarantee: extra === 0,
        headline: fill(T.arr_tight_head, { day }),
        when: fill(T.arr_tight_when, { day, md: md(ret) }),
        deadline: fill(T.arr_tight_slot, { morning }),
      };
    }
    const last = shipDayFor(schedule, pref, addDays(ret, -1));
    return { state: 'ok', headline: T.arr_ok, guarantee: extra === 0, when: arrivalWhen(schedule, pref, T), deadline: fill(T.arr_deadline_day_before, { md: md(last) }), before: T.arr_before };
  }
  function arrivalWhen(schedule, pref, T) {
    const extra = schedule.extraDays(pref);
    const earliest = schedule.earliestDelivery(pref);
    const ship = addDays(earliest, -1 - extra);
    const tomorrow = addDays(schedule.today, 1);
    const md = (d) => dateText(d, T, 'date_md');
    const dayWord = ship === schedule.today ? T.arr_today : ship === tomorrow ? T.arr_tomorrow : md(ship);
    const delKey = earliest === tomorrow ? 'arr_del_tomorrow' : earliest === addDays(schedule.today, 2) ? 'arr_del_after' : 'arr_del_date';
    return fill(T.arr_when, { day: dayWord, del: fill(T[delKey], { md: md(earliest) }) });
  }
  // the receiving step: judged by the delivery date chosen (earliest ≤ date; before the return day / check-out day, or
  // on that day itself = tight, morning only, while schedule.returnMorning is on). opts: { checkout }
  // A date before the earliest day: the earliest day with a button to take it (fix) when it still fits the stay, and
  // Osaka pick-up for sooner when it can be done by the date wanted.
  function arrivalByDate(schedule, pref, date, ret, T, opts = {}) {
    if (!pref) return { state: 'empty' };
    const earliest = schedule.earliestDelivery(pref);
    const md = (d) => dateText(d, T, 'date_md');
    const checkout = validDate(opts.checkout) ? opts.checkout : '';
    const by = ret && validDate(ret) ? ret : checkout;
    if (!date) return { state: 'info', headline: fill(T.arr_pick_date, { date: dateText(earliest, T) }) };
    if (!validDate(date) || date < schedule.today) return { state: 'ng', invalid: true, headline: T.arr_invalid };
    const ng = (headline, more = {}) => ({ state: 'ng', soft: true, headline, ...more });
    if (ret && validDate(ret) && date > ret) return ng(T.arr_before_return);
    if (date < earliest) {
      // the stay ends at the check-out day or the return day, whichever comes first
      const limit = [ret, checkout].filter((d) => d && validDate(d)).sort()[0] || '';
      const fits = !limit || earliest < limit || (earliest === limit && schedule.returnMorning);
      const osaka = osakaLine(schedule, fits ? date : limit, T);
      if (fits) return ng(fill(T.arr_earliest_is, { date: dateText(earliest, T) }), { when: T.arr_pick_later, fix: earliest, osaka });
      return ng(fill(T.arr_ng_head, { date: dateText(earliest, T) }), { when: osaka ? '' : T.arr_ng_body, osaka });
    }
    const onReturn = ret && date === ret;
    const onCheckout = checkout && date === checkout;
    if ((onReturn || onCheckout) && !schedule.returnMorning) return ng(onReturn ? T.arr_before_return : T.arr_before_checkout);
    const last = shipDayFor(schedule, pref, date);
    const deadline = fill(T.arr_date_deadline, { last: md(last), date: md(date) });
    if (onReturn || onCheckout) {
      return {
        state: 'ok', tight: true,
        headline: fill(T.arr_tight_head, { day: dayWord(schedule, last, T) }),
        when: fill(onReturn ? T.arr_tight_date : T.arr_tight_checkout, { md: md(date), morning: T.slot_morning || '' }),
        deadline,
      };
    }
    return { state: 'ok', headline: T.arr_ok, when: arrivalWhen(schedule, pref, T), deadline, before: T.arr_before };
  }
  // the return date is asked of travellers: a delivery must come by it (on the day itself only in the morning, while
  // that is accepted); a pick-up at the clinic must be on it or before (owner 2026-10-22)
  function returnIssue(schedule, s, T) {
    if (!s || s.stay !== 'traveling') return null;
    if (s.method === 'pickup') {
      if (!validDate(s.returnDate)) return { field: 'returnDate', message: T.err_return };
      if (validDate(s.pickupDate) && s.returnDate < s.pickupDate) return { field: 'returnDate', message: T.err_return_pickup };
      return null;
    }
    if (s.method !== 'delivery') return null;
    if (!s.returnDate) return { field: 'returnDate', message: T.err_return };
    const a = arrivalByDate(schedule, s.pref, s.date, s.returnDate, T, { checkout: s.checkout });
    if (a.state === 'ng') return { field: 'returnDate', message: a.headline };
    if (a.tight && s.date === s.returnDate && s.time !== 'morning') return { field: 'time', message: fill(T.err_return_morning, { morning: T.slot_morning || '' }) };
    return null;
  }

  // ---------- carry check (advisory only; never an entry approval) ----------
  // Pens of the same carry group in the cart: pack_pens × quantity per line; certificate and other medicines excluded.
  function cartPens(items, known, group, excludeKey) {
    let total = 0;
    let unknown = !group;
    for (const line of items || []) {
      if (line.key === excludeKey) continue;
      const info = known.get(line.variant_id);
      if (!info) {
        unknown = true;
        continue;
      }
      if (info.group !== group) continue;
      if (info.pens == null) unknown = true;
      else total += info.pens * line.quantity;
    }
    return { total, unknown };
  }
  // count: number, null = invalid custom input, undefined = cannot be calculated
  function carryCount(s, orderTotal) {
    if (s.destination === 'jp') return 0;
    if (s.carryMode !== 'custom') return orderTotal == null ? undefined : orderTotal;
    return /^\d{1,3}$/.test(s.carryPens) && Number(s.carryPens) <= 999 ? Number(s.carryPens) : null;
  }
  function carryIssue(s, count, ctx) {
    if (!Object.hasOwn(ctx.rules, s.destination)) return { field: 'destination', message: ctx.T.carry_err_destination };
    if (count === null) return { field: 'carryPens', message: ctx.T.carry_err_pens };
    if (count === undefined) return { field: 'destination', message: ctx.T.carry_status_unknown };
    return null;
  }
  // ctx: { rules, T, hub, reviewOnly } — reviewOnly: no medicine with a carry group (only Mounjaro has rules of pens
  // today), so the quantity cannot be judged: 「持ち込み条件を確認」 and 「—」, shown only, never a stop (plan v3 §3E).
  // limit: the destination's limit line in red (F-38).
  function carryAssessment(s, count, ctx) {
    const { T } = ctx;
    const rule = ctx.rules[s.destination];
    const issue = carryIssue(s, count, ctx);
    const abroad = Boolean(rule) && s.destination !== 'jp';
    const result = {
      state: 'unselected',
      destination: rule ? rule.name : '',
      quantity: count == null ? '—' : fill(T.carry_quantity, { count }),
      status: T.carry_status_unselected,
      note: T.carry_note_unselected,
      basis: '',
      limit: abroad ? (rule.period ? fill(T.carry_limit, { period: rule.period }) : T.carry_limit_none || '') : '',
      guide: rule?.guide || ctx.hub,
    };
    if (abroad && ctx.reviewOnly) return { ...result, state: 'review', quantity: '—', status: T.carry_review_status, note: T.carry_review_note, basis: '' };
    if (count === undefined && rule) return { ...result, state: 'invalid', status: T.carry_status_unknown, note: T.carry_note_unknown };
    if (issue) {
      if (issue.field === 'carryPens') Object.assign(result, { state: 'invalid', status: T.carry_status_invalid, note: issue.message });
      return result;
    }
    if (s.destination === 'jp' || count === 0) return { ...result, state: 'domestic', status: T.carry_status_domestic, note: T.carry_note_domestic };
    Object.assign(result, { state: 'review', status: T.carry_status_review, note: rule.note || '', basis: T.carry_basis_review });
    if (rule.maxDays) {
      const days = count * 7;
      result.basis = fill(T.carry_basis_days, { days, period: rule.period || '' });
      // over: the box's line (country, the guide, about how many months now: 4 pens a month) and the most pens 1 person
      // may carry (owner 2026-10-21: shown at once, with one tap to fit and how 2 people order)
      if (days > rule.maxDays) {
        Object.assign(result, {
          state: 'over',
          status: T.carry_status_over,
          note: `${T.carry_note_over}${rule.note || ''}`,
          ruleNote: rule.note || '',
          overBody: fill(T.carry_over_body || '', { country: rule.name, period: rule.period || '', months: Math.round((count / 4) * 10) / 10 }),
          maxPens: Math.floor(rule.maxDays / 7),
        });
      } else if (days > rule.minDays) {
        Object.assign(result, { state: 'boundary', status: T.carry_status_boundary, note: `${T.carry_note_boundary}${rule.note || ''}` });
      } else if (!rule.review) {
        Object.assign(result, { state: 'documents', status: T.carry_status_documents });
      }
    }
    return result;
  }

  // ---------- server cart ----------
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  async function request(url, body) {
    const res = await fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'same-origin',
    });
    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      data = null;
    }
    return { ok: res.ok, status: res.status, data };
  }
  async function fetchCart(cartUrl) {
    for (let i = 0; i < 4; i++) {
      try {
        const res = await request(`${cartUrl}.js`);
        if (res.ok && res.data) return res.data;
        if (res.status !== 429) return null;
      } catch (e) {
        /* network: retry */
      }
      await sleep(900 * (i + 1));
    }
    return null;
  }
  const quantityOf = (cart, variantId) => (cart.items || []).filter((l) => l.variant_id === variantId).reduce((n, l) => n + l.quantity, 0);
  const attributesMatch = (cart, attrs) => Object.entries(attrs).every(([k, v]) => (cart.attributes?.[k] ?? '') === v);
  // Saves attributes and confirms them by reading the cart back; returns the cart, or null when not confirmed.
  async function saveAttributes(routes, attrs) {
    let last = null;
    for (let i = 0; i < 3; i++) {
      try {
        await request(`${routes.cartUpdate}.js`, { attributes: attrs });
      } catch (e) {
        /* checked below */
      }
      last = await fetchCart(routes.cart);
      if (last && attributesMatch(last, attrs)) return last;
      await sleep(700 * (i + 1));
    }
    return null;
  }
  // One change at a time (contract operations.concurrency)
  function serialQueue() {
    let queue = Promise.resolve();
    return (task) => {
      const run = queue.then(task, task);
      queue = run.catch(() => {});
      return run;
    };
  }

  // R02 (D05): preselect the checkout's delivery method (and pickup location) and prefill the prefecture from the
  // receipt, through the Storefront API (buyer delivery preferences and the cart's delivery address). Checkout
  // still lets the customer change everything; when this fails the customer simply chooses at checkout, so a
  // failure never blocks the order. Verified 2026-10-12: checkout opens with 都道府県 already selected.
  const PREFECTURES = ['北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県', '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県', '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県', '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県', '鳥取県', '島根県', '岡山県', '広島県', '山口県', '徳島県', '香川県', '愛媛県', '高知県', '福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県'];
  // the shown name of a prefecture in the page's language (names: the 47 of jwc.prefectures in the usual order, or null)
  const prefLabel = (names) => (name) => {
    const i = PREFECTURES.indexOf(name);
    return Array.isArray(names) && names[i] ? String(names[i]).trim() : name;
  };
  const provinceCode = (name) => {
    const i = PREFECTURES.indexOf(name);
    return i < 0 ? null : `JP-${String(i + 1).padStart(2, '0')}`;
  };
  async function storefront(store, query, variables) {
    const res = await fetch(`/api/${store.apiVersion}/graphql.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Shopify-Storefront-Access-Token': store.token },
      body: JSON.stringify({ query, variables }),
      credentials: 'same-origin',
    });
    return res.json();
  }
  // F-66: checkout's address from the receipt: the passport's surname / given names, and for a hotel its name as the
  // building (address2), its phone (the waybill's number, okurun §4) and the prefecture; the street address only when
  // the hotel search found it for this very hotel name (F-67). For the Osaka clinic, the names only.
  // The names are the two fields as typed (saved on the cart, or this browser's prefill for the same passport name). An
  // older cart's passport_name alone is not split at a space (JWC-08): one word is the surname, more are left for
  // checkout to ask.
  function checkoutAddress(r, prefill) {
    const passport = passportName(r.passport);
    const same = prefill && prefill.passport === passport && prefill.lastName;
    const words = passport ? passport.split(' ') : [];
    const a = { countryCode: 'JP' };
    const lastName = r.passportSurname ? passportName(r.passportSurname) : same ? prefill.lastName : words.length === 1 ? words[0] : '';
    const firstName = r.passportSurname ? passportName(r.passportGiven) : same ? prefill.firstName : '';
    if (lastName) a.lastName = lastName;
    if (firstName) a.firstName = firstName;
    if (r.method === 'pickup') return a;
    const code = provinceCode(r.pref);
    if (code) a.provinceCode = code;
    if (r.stay !== 'traveling') return a;
    const hotel = String(r.hotel || '').trim();
    if (hotel) a.address2 = hotel;
    const phone = japanPhone(r.phone);
    if (phone.ok) a.phone = `+81${phone.value.slice(1)}`;
    const found = prefill?.found;
    if (found && found.hotel === hotel && found.pref === r.pref) {
      if (found.zip) a.zip = found.zip;
      if (found.city) a.city = found.city;
      if (found.address1) a.address1 = found.address1;
    }
    return a;
  }
  // Which receipt the cart's checkout address was last set from (this browser, one cart: another cart's record is never
  // used). Written only after the address was replaced without errors (okurun answer §6, 2026-10-26).
  const APPLIED_KEY = 'jwc-delivery-applied';
  const normText = (s) => String(s || '').normalize('NFKC').toLowerCase().replace(/\s+/g, ' ').trim();
  const deliveryKey = (r) => {
    const pickup = r.method === 'pickup';
    const traveling = r.stay !== 'resident';
    return { method: pickup ? 'pickup' : 'delivery', stay: traveling ? 'traveling' : 'resident', pref: pickup ? '' : r.pref || '', hotel: !pickup && traveling ? normText(r.hotel) : '' };
  };
  const sameKey = (a, b) => ['method', 'stay', 'pref', 'hotel'].every((k) => (a?.[k] || '') === (b?.[k] || ''));
  function readApplied(cartToken) {
    try {
      const a = JSON.parse(localStorage.getItem(APPLIED_KEY) || 'null');
      return a && a.cart === cartToken ? a : null;
    } catch (e) {
      return null;
    }
  }
  function writeApplied(cartToken, key, address) {
    try {
      localStorage.setItem(APPLIED_KEY, JSON.stringify({ cart: cartToken, ...key, zip: address.zip || '', address1: address.address1 || '', at: Date.now() }));
    } catch (e) {
      /* the next checkout decides from the address alone */
    }
  }
  // Whether the address already selected for checkout stays: only the customer's own address for this same receipt —
  // the same hotel (its name in the address, or the postal code / street set from it last time and edited at checkout:
  // a room number, a corrected street), or a resident's own address in the same prefecture (a building name is never
  // compared with a hotel name). Another hotel in the same prefecture (hotel B under hotel A's street on the waybill),
  // another cart's address, or a case that cannot be told apart is replaced with this receipt.
  function keepCheckoutAddress(r, selected, applied) {
    if (r.method === 'pickup' || !selected?.address1) return false;
    const code = provinceCode(r.pref);
    if (!code || selected.provinceCode !== code) return false;
    const key = deliveryKey(r);
    if (applied && !sameKey(applied, key)) return false; // the receipt changed since the address was last set
    if (key.stay === 'resident') return true;
    const named = Boolean(key.hotel) && [selected.address1, selected.address2].some((x) => normText(x).includes(key.hotel));
    const edited = Boolean(applied) && Boolean((applied.zip && normText(selected.zip) === normText(applied.zip)) || (applied.address1 && normText(selected.address1).startsWith(normText(applied.address1))));
    return named || edited;
  }
  // → { ok, replaced, blocking }. blocking: an earlier street (another hotel's, another cart's) is still selected because
  // replacing it failed — checkout must not start from it (jwc-cart.js stops and asks to try again)
  async function preselectDelivery(store, cart, r, prefill) {
    if (!store || !store.token || !cart?.token || !r) return { ok: false, reason: 'not configured' };
    const id = `gid://shopify/Cart/${cart.token}`;
    const pickup = r.method === 'pickup';
    const delivery = { deliveryMethod: [pickup ? 'PICK_UP' : 'SHIPPING'] };
    if (pickup && store.pickupLocation) delivery.pickupHandle = [String(store.pickupLocation)];
    let stale = false;
    try {
      const current = await storefront(store, 'query($id: ID!) { cart(id: $id) { delivery { addresses { selected address { ... on CartDeliveryAddress { provinceCode zip city address1 address2 } } } } } }', { id });
      const selected = current?.data?.cart?.delivery?.addresses?.find((a) => a.selected)?.address;
      const code = pickup ? null : provinceCode(r.pref);
      // delivery: checkout starts from the receipt (F-66) unless the selected address is the customer's own for this same
      // receipt (keepCheckoutAddress). The Osaka clinic: always the names only (an earlier address is dropped, as before)
      const replace = pickup ? true : Boolean(code) && !keepCheckoutAddress(r, selected, readApplied(cart.token));
      stale = replace && !pickup && Boolean(selected?.address1);
      const address = checkoutAddress(r, prefill);
      const addresses = pickup || code ? [{ address: { deliveryAddress: address }, selected: true, oneTimeUse: false }] : [];
      const data = await storefront(store, `mutation($id: ID!, $b: CartBuyerIdentityInput!${replace ? ', $a: [CartSelectableAddressInput!]!' : ''}) {
          pref: cartBuyerIdentityUpdate(cartId: $id, buyerIdentity: $b) { cart { id } userErrors { field message code } }
          ${replace ? 'addr: cartDeliveryAddressesReplace(cartId: $id, addresses: $a) { userErrors { field message code } }' : ''}
        }`, { id, b: { countryCode: 'JP', preferences: { delivery } }, ...(replace ? { a: addresses } : {}) });
      const errors = [...(data?.errors || []), ...(data?.data?.pref?.userErrors || []), ...(data?.data?.addr?.userErrors || [])];
      const ok = !errors.length && Boolean(data?.data?.pref?.cart);
      if (ok && replace) writeApplied(cart.token, deliveryKey(r), address);
      return { ok, replaced: replace, blocking: !ok && stale, reason: errors.map((e) => e.message).join(' / ') };
    } catch (e) {
      return { ok: false, blocking: stale, reason: e.message };
    }
  }

  async function sha256(text) {
    const bytes = new TextEncoder().encode(text);
    const hash = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(hash), (b) => b.toString(16).padStart(2, '0')).join('');
  }
  // JSON with object keys sorted at every level, so the same content always gives the same digest
  function stableJson(value) {
    if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
    if (value && typeof value === 'object') return `{${Object.keys(value).sort().map((k) => `${JSON.stringify(k)}:${stableJson(value[k])}`).join(',')}}`;
    return JSON.stringify(value ?? null);
  }

  return {
    ATTR, ENGLISH_KEYS, RECEIPT_KEYS, decode, texts, fill, intlLocale, decodeRules, money, fxText,
    toMs, addDays, validDate, setTestClock, tokyoNow, formatDate, dateText, nights, lines, makeSchedule, countdown,
    slotJa, passportName, validPassportName, japanPhone,
    receiptFromAttributes, receiptIssue, receiptValues, receiptAttributes, certificateIssue, certificateProperties, CERT_SEX,
    arrival, arrivalByDate, returnIssue,
    cartPens, carryCount, carryIssue, carryAssessment,
    sleep, request, fetchCart, quantityOf, attributesMatch, saveAttributes, serialQueue, sha256, stableJson, preselectDelivery, keepCheckoutAddress, checkoutAddress, prefLabel,
  };
})();
